import type { DocumentTemplateCategory, DocumentTemplateKind } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { t } from '@/i18n';

/**
 * Variable palette (Task 6, spec §8.3): click-to-insert chips grouped
 * firma/pracownik/kontrahent/dokument, filtered by the template's category.
 *
 * `apps/backend/src/modules/documents/template-variables.ts` is the SOURCE OF
 * TRUTH for the variable registry; this file duplicates the key LIST
 * only (grouped) plus the category→groups mapping, for client-side rendering
 * and an editor-side "unknown variable" hint. Any drift must be fixed there
 * first, then mirrored here.
 */

export type VariableGroup = 'firma' | 'pracownik' | 'kontrahent' | 'dokument' | 'wystawca' | 'umowa';

export const VARIABLE_GROUPS: Record<VariableGroup, string[]> = {
  firma: [
    'firma.nazwa',
    'firma.nip',
    'firma.bdo',
    'firma.krs',
    'firma.email',
    'firma.www',
    'firma.ulica',
    'firma.kod',
    'firma.miasto',
  ],
  pracownik: [
    'pracownik.imie',
    'pracownik.nazwisko',
    'pracownik.ulica',
    'pracownik.kod',
    'pracownik.miasto',
    'pracownik.adres',
    'pracownik.pesel',
    'pracownik.stanowisko',
    'pracownik.typ_umowy',
    'pracownik.data_zatrudnienia',
    'pracownik.data_urodzenia',
    'pracownik.urzad_skarbowy',
    'pracownik.rachunek_bankowy',
  ],
  kontrahent: ['kontrahent.nazwa', 'kontrahent.nip', 'kontrahent.adres'],
  dokument: ['dokument.data', 'dokument.wersja', 'dokument.numer'],
  wystawca: ['wystawca.imie_nazwisko', 'wystawca.email'],
  // Contract data supplied by the publisher of a templateKind='umowa_zlecenie'
  // template (umowa spec 2026-07-23) — employee-only group.
  umowa: [
    'umowa.data_zawarcia',
    'umowa.data_od',
    'umowa.data_do',
    'umowa.stawka',
    'umowa.reprezentacja',
  ],
};

const GROUP_ORDER: VariableGroup[] = ['firma', 'pracownik', 'kontrahent', 'dokument', 'wystawca', 'umowa'];

/**
 * Gender-branch snippets (`{{plec:męska/żeńska}}` — resolved from the
 * employee's kartoteka sex at render time; unknown sex prints both forms).
 * Inserted as plain text, not as variable chips.
 */
export const PLEC_SNIPPETS: string[] = [
  '{{plec:Pan/Pani}}',
  '{{plec:Pana/Panią}}',
  '{{plec:zamieszkałego/zamieszkałą}}',
  '{{plec:zatrudnionego/zatrudnioną}}',
];

const VARIABLE_TO_GROUP: Record<string, VariableGroup> = Object.fromEntries(
  GROUP_ORDER.flatMap((group) => VARIABLE_GROUPS[group].map((key) => [key, group])),
);

// Duplicate of groupsForCategory() in template-variables.ts. The 'umowa'
// group resolves only from publication contract data, which exists solely for
// templateKind='umowa_zlecenie' — standard templates never get to see it.
export function groupsForCategory(
  category: DocumentTemplateCategory | string,
  templateKind?: DocumentTemplateKind | null,
): VariableGroup[] {
  const groups: VariableGroup[] = (() => {
    switch (category) {
      case 'employee':
        return ['firma', 'pracownik', 'dokument', 'wystawca', 'umowa'];
      case 'b2b':
        return ['firma', 'pracownik', 'kontrahent', 'dokument', 'wystawca'];
      case 'contractor':
        return ['firma', 'kontrahent', 'dokument', 'wystawca'];
      default:
        return ['firma', 'dokument', 'wystawca'];
    }
  })();
  return templateKind === 'umowa_zlecenie' ? groups : groups.filter((g) => g !== 'umowa');
}

const VARIABLE_PATTERN = /\{\{\s*([a-z_]+\.[a-z_]+)\s*\}\}/g;
// Mirrors PLEC_PATTERN_SOURCE / findMalformedPlecTokens in template-variables.ts.
const PLEC_PATTERN = /\{\{\s*plec:\s*([^/{}\n]*[^/{}\s])\s*\/\s*([^/{}\n]*[^/{}\s])\s*\}\}/g;
const PLEC_LIKE = /\{\{\s*plec\s*[:}][^]{0,40}/g;

/** Duplicate of findUnknownVariables() in template-variables.ts, for an editor-side hint. */
export function findUnknownVariablesClient(
  contentMd: string,
  category: DocumentTemplateCategory | string,
  templateKind?: DocumentTemplateKind | null,
): string[] {
  const allowedGroups = new Set(groupsForCategory(category, templateKind));
  const seen = new Set<string>();
  const unknown: string[] = [];
  const re = new RegExp(VARIABLE_PATTERN);
  let match: RegExpExecArray | null;
  while ((match = re.exec(contentMd)) !== null) {
    const name = match[1];
    if (seen.has(name)) continue;
    seen.add(name);
    const group = VARIABLE_TO_GROUP[name];
    if (!group || !allowedGroups.has(group)) unknown.push(name);
  }
  // Malformed {{plec…}} tokens (missing/empty form, newline, stray slash) —
  // surfaced in the same editor warning; the backend rejects them on publish.
  const stripped = contentMd.replace(new RegExp(PLEC_PATTERN), '');
  let bad: RegExpExecArray | null;
  const badRe = new RegExp(PLEC_LIKE);
  while ((bad = badRe.exec(stripped)) !== null) {
    const raw = bad[0].split('\n')[0];
    const end = raw.indexOf('}}');
    unknown.push(end >= 0 ? raw.slice(0, end + 2) : raw);
  }
  return unknown;
}

interface VariablePaletteProps {
  category: DocumentTemplateCategory;
  /** Gates the 'umowa' group — offered only for umowa_zlecenie templates. */
  templateKind?: DocumentTemplateKind | null;
  onInsert: (variable: string) => void;
  /** Inserts raw text (not a variable chip) — used by the gender snippets. */
  onInsertText?: (text: string) => void;
}

export function VariablePalette({ category, templateKind, onInsert, onInsertText }: VariablePaletteProps) {
  const groups = groupsForCategory(category, templateKind);
  const showPlec = onInsertText && groups.includes('pracownik');

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">{t('documents.editor.insertHint')}</p>
      {groups.map((group) => (
        <div key={group} className="flex flex-col gap-1.5">
          <p className="text-muted-foreground text-xs font-medium uppercase">
            {t(`documents.editor.groups.${group}`)}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {VARIABLE_GROUPS[group].map((variable) => (
              <Tooltip key={variable}>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    asChild
                    className="hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  >
                    <button type="button" onClick={() => onInsert(variable)}>
                      {t(`documents.editor.vars.${variable}`)}
                    </button>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="font-mono text-xs">{`{{${variable}}}`}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>
      ))}
      {showPlec && (
        <div className="flex flex-col gap-1.5">
          <p className="text-muted-foreground text-xs font-medium uppercase">
            {t('documents.editor.groups.plec')}
          </p>
          <p className="text-muted-foreground text-xs">{t('documents.editor.plecHint')}</p>
          <div className="flex flex-wrap gap-1.5">
            {PLEC_SNIPPETS.map((snippet) => (
              <Tooltip key={snippet}>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    asChild
                    className="hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  >
                    <button type="button" onClick={() => onInsertText(snippet)}>
                      {snippet.replace(/^\{\{plec:|\}\}$/g, '')}
                    </button>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="font-mono text-xs">{snippet}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
